import {CSSProperties, useMemo} from "react";
import {computeAltitude} from "@game/map/geography";
import {TILE_SIZE} from "@components/core/theme";

const SVG_W = 3000;
const SVG_H = 2400;
const HALF_W = SVG_W / 2;
const HALF_H = SVG_H / 2;

// Recenter the SVG origin when the view drifts beyond this distance (half the SVG radius).
export const CONTOUR_RECENTER_X = HALF_W / 2;
export const CONTOUR_RECENTER_Y = HALF_H / 2;

// Cell size in tiles — 0.5 gives double the segment density vs 1 tile/cell,
// producing smoother curves without a significant perf cost.
const STEP = 0.5;

// One contour level every 0.06 across [0.10, 0.88] — ensures lines appear
// wherever in the altitude range the local terrain happens to sit.
const CONTOUR_LEVELS = Array.from({length: 14}, (_, i) => 0.10 + i * 0.06);
const STROKE = "rgba(61,43,31,0.25)";
const STROKE_WIDTH = 1.3;

// Marching squares case table: [edge1, edge2] pairs per 4-bit index.
// Bit order: TL=8, TR=4, BR=2, BL=1 (1 = above threshold).
const CASES: [string, string][][] = [
    [],
    [["bottom", "left"]],
    [["right", "bottom"]],
    [["right", "left"]],
    [["top", "right"]],
    [["top", "right"], ["bottom", "left"]],
    [["top", "bottom"]],
    [["top", "left"]],
    [["top", "left"]],
    [["top", "bottom"]],
    [["top", "left"], ["right", "bottom"]],
    [["top", "right"]],
    [["right", "left"]],
    [["right", "bottom"]],
    [["bottom", "left"]],
    [],
];

interface IContourLines {
    originX: number
    originY: number
    seed: number
}

export function ContourLines({originX, originY, seed}: IContourLines) {
    const paths = useMemo(() => {
        // Tile-space bounds of the SVG
        const originTX = originX / TILE_SIZE.x;
        const originTY = originY / TILE_SIZE.y;
        const halfTW = HALF_W / TILE_SIZE.x;
        const halfTH = HALF_H / TILE_SIZE.y;

        const minCX = Math.floor((originTX - halfTW) / STEP) * STEP;
        const minCY = Math.floor((originTY - halfTH) / STEP) * STEP;
        const cols = Math.ceil((originTX + halfTW - minCX) / STEP) + 1;
        const rows = Math.ceil((originTY + halfTH - minCY) / STEP) + 1;

        // Pre-build altitude grid so each point is computed once across all levels
        const grid: number[][] = Array.from({length: cols + 1}, (_, xi) =>
            Array.from({length: rows + 1}, (_, yi) =>
                computeAltitude(minCX + xi * STEP, minCY + yi * STEP, seed)
            )
        );

        const lerp = (a: number, b: number, t: number) =>
            Math.abs(b - a) < 1e-10 ? 0.5 : (t - a) / (b - a);

        const toSvg = (tx: number, ty: number): [number, number] => [
            tx * TILE_SIZE.x - originX + HALF_W,
            ty * TILE_SIZE.y - originY + HALF_H,
        ];

        const result: {level: number, d: string}[] = [];

        for (const threshold of CONTOUR_LEVELS) {
            const parts: string[] = [];

            for (let xi = 0; xi < cols; xi++) {
                for (let yi = 0; yi < rows; yi++) {
                    const cx = minCX + xi * STEP;
                    const cy = minCY + yi * STEP;
                    const aTL = grid[xi][yi];
                    const aTR = grid[xi + 1][yi];
                    const aBL = grid[xi][yi + 1];
                    const aBR = grid[xi + 1][yi + 1];

                    const idx = ((aTL > threshold ? 1 : 0) << 3) |
                                ((aTR > threshold ? 1 : 0) << 2) |
                                ((aBR > threshold ? 1 : 0) << 1) |
                                ((aBL > threshold ? 1 : 0));

                    const segs = CASES[idx];
                    if (segs.length === 0) continue;

                    const edgePt = (edge: string): [number, number] => {
                        switch (edge) {
                            case "top":    return [cx + lerp(aTL, aTR, threshold) * STEP, cy];
                            case "right":  return [cx + STEP, cy + lerp(aTR, aBR, threshold) * STEP];
                            case "bottom": return [cx + lerp(aBL, aBR, threshold) * STEP, cy + STEP];
                            case "left":   return [cx, cy + lerp(aTL, aBL, threshold) * STEP];
                            default:       return [cx + STEP / 2, cy + STEP / 2];
                        }
                    };

                    for (const [e1, e2] of segs) {
                        const [tx1, ty1] = edgePt(e1);
                        const [tx2, ty2] = edgePt(e2);
                        const [sx1, sy1] = toSvg(tx1, ty1);
                        const [sx2, sy2] = toSvg(tx2, ty2);
                        parts.push(`M ${sx1.toFixed(1)},${sy1.toFixed(1)} L ${sx2.toFixed(1)},${sy2.toFixed(1)}`);
                    }
                }
            }

            if (parts.length > 0) {
                result.push({level: threshold, d: parts.join(" ")});
            }
        }

        return result;
    }, [originX, originY, seed]);

    const style: CSSProperties = {
        position: "absolute",
        left: originX - HALF_W,
        top: originY - HALF_H,
        pointerEvents: "none",
        mixBlendMode: "multiply"
    };

    return (
        <svg style={style} width={SVG_W} height={SVG_H}>
            <g>
                {paths.map(({level, d}) => (
                    <path key={level} d={d} stroke={STROKE} strokeWidth={STROKE_WIDTH} fill="none"/>
                ))}
            </g>
        </svg>
    );
}
